/**
 * The granted card's margin worksheet (`/rate-cards/:id`, Worksheet tab, TMMRSL-0075, TMMAPI-0154
 * §H): a hypothetical customer, their bill and what the reseller earns, per month. Read-only.
 *
 *  - The inputs (mode, guests, bank connections, AI used) live in the URL (`mode`, `guests`,
 *    `connections`, `ai`; defaults omitted), so a what-if is shareable. Mode, a count, or AI is
 *    written only when that value is valid and sent, on the debounce or on blur; while a field
 *    is invalid the URL keeps the last sent value (TMMRSL-0082). Guests and connections
 *    default to the card's included counts from the last answer's terms (0 before the first).
 *  - Each change re-asks tmm-api 300 ms after the last one, and leaving a field at once, but only
 *    while every input is valid (TMMRSL-0077). An older answer never overwrites a
 *    newer one (`useRemote`), and while a new one is on its way the last stays on screen, marked
 *    stale (`BusyRegion`, "Updating…"). An invalid input is marked as it is typed and never sent;
 *    the results stay marked stale ("Results are for the last valid inputs") until it is fixed.
 *  - "What your customer pays" is the server's `bill` (TMMAPI-0155): its lines, then its two
 *    subtotals. "How it's worked out" lists the terms and the `steps` as sent, and "Your profit"
 *    carries `resellerProfitBp` as a percent (none when the customer pays 0).
 *  - **The composition rule: every number is the server's.** The screen does no arithmetic: no
 *    sums, differences, per-month division or percentages. The platform's cost and profit are
 *    never sent to a reseller and are never named here.
 */
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import {
  Box,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { mapFieldErrors } from '@/api/fieldErrors';
import {
  rateCardsApi,
  type Bill,
  type GrantedCard,
  type Worksheet,
  type WorksheetLine,
  type WorksheetLineKind,
  type WorksheetMode,
} from '@/api/rateCards';
import type { WorksheetBillLine, WorksheetRequest } from '@/api/generated';
import { ApiErrorState } from '@/components/ApiErrorState';
import { BusyRegion } from '@/components/BusyRegion';
import { AccountingAmount, AccountingMoneyCell, TotalsFooter } from '@/components/cells';
import { DefinitionField, DefinitionList } from '@/components/DefinitionList';
import { moneyIn } from '@/components/moneyIn';
import { describeAction } from '@/components/useActionError';
import { useFieldErrors } from '@/components/useFieldErrors';
import { useRemote } from '@/components/useRemote';
import { enumParam, intParam, useUrlState, type UrlParam } from '@/components/useUrlState';
import {
  bpToPercentText,
  formatCount,
  formatMoneyAccounting,
  minorToMoneyInput,
  moneyInputExample,
  parseMoneyToMinor,
} from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';

const DEBOUNCE_MS = 300;
const MAX_COUNT = 50;

/** A count 0–50, or `null` (absent: the card's included count). */
function countParam(): UrlParam<number | null> {
  return {
    defaultValue: null,
    parse: (raw) => {
      if (!/^\d{1,2}$/.test(raw)) return undefined;
      const value = Number(raw);
      return value <= MAX_COUNT ? value : undefined;
    },
    serialize: (value) => String(value),
  };
}

const MODES = ['MONTHLY', 'ANNUAL'] as const satisfies readonly WorksheetMode[];

const SCHEMA = {
  mode: enumParam(MODES, 'MONTHLY'),
  guests: countParam(),
  connections: countParam(),
  /** AI used per month at the customer's price, in minor units. */
  ai: intParam(0),
};

type Field = 'guests' | 'connections' | 'ai';
const FIELD_ORDER: readonly Field[] = ['guests', 'connections', 'ai'];
const SERVER_FIELDS: Readonly<Record<string, Field>> = {
  guests: 'guests',
  connections: 'connections',
  aiUsedMinor: 'ai',
};

/** Each known line kind's label; anything else is `UNKNOWN`, never the raw enum. */
const LINE_LABELS: Readonly<Record<WorksheetLineKind, string>> = {
  BASE: 'rateCards.worksheet.line.BASE',
  GUESTS: 'rateCards.worksheet.line.GUESTS',
  CONNECTIONS: 'rateCards.worksheet.line.CONNECTIONS',
  SUBSCRIPTION_PROCESSING: 'rateCards.worksheet.line.SUBSCRIPTION_PROCESSING',
  AI_PAID: 'rateCards.worksheet.line.AI_PAID',
  AI_CREDIT: 'rateCards.worksheet.line.AI_CREDIT',
  RECHARGE_PROCESSING: 'rateCards.worksheet.line.RECHARGE_PROCESSING',
  TOTAL: 'rateCards.worksheet.line.TOTAL',
};

function lineLabel(line: string, t: TFunction): string {
  return t(
    Object.hasOwn(LINE_LABELS, line)
      ? LINE_LABELS[line as WorksheetLineKind]
      : 'rateCards.worksheet.line.UNKNOWN',
  );
}

function parseCount(text: string): number | null {
  const trimmed = text.trim();
  if (!/^\d{1,2}$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return value <= MAX_COUNT ? value : null;
}

/**
 * URL keys for inputs that differ from what the URL already shows. An omitted default (the
 * card's included count, or 0 AI) is already that value, so it is not written.
 */
function sentInputPatch(
  next: WorksheetRequest,
  shown: { mode: WorksheetMode; guests: number; connections: number; ai: number },
): { mode?: WorksheetMode; guests?: number; connections?: number; ai?: number } | null {
  const patch: { mode?: WorksheetMode; guests?: number; connections?: number; ai?: number } = {};
  if (next.mode !== null && next.mode !== shown.mode) patch.mode = next.mode;
  if (next.guests !== null && next.guests !== shown.guests) patch.guests = next.guests;
  if (next.connections !== null && next.connections !== shown.connections) {
    patch.connections = next.connections;
  }
  if (next.aiUsedMinor !== null && next.aiUsedMinor !== shown.ai) patch.ai = next.aiUsedMinor;
  if (
    patch.mode === undefined &&
    patch.guests === undefined &&
    patch.connections === undefined &&
    patch.ai === undefined
  ) {
    return null;
  }
  return patch;
}

/**
 * A field's text: what was typed, until the value changes to one the text doesn't already say
 * (Back, a link, the card's default arriving). Typing "9.5" keeps "9.5", never "9.50". When the
 * text is reset its error no longer applies: `onReset` clears it (TMMRSL-0077). A value this
 * screen just wrote (`isOwnWrite`) does not reset text the user has changed since that write.
 */
function useDraft(
  value: number,
  format: (value: number) => string,
  parse: (text: string) => number | null,
  onReset: () => void,
  isOwnWrite: (value: number) => boolean,
): [string, (next: string) => void] {
  const [draft, setDraft] = useState(() => format(value));
  const [seen, setSeen] = useState(value);
  if (seen !== value) {
    setSeen(value);
    if (parse(draft) !== value && !isOwnWrite(value)) {
      setDraft(format(value));
      onReset();
    }
  }
  return [draft, setDraft];
}

export function RateCardWorksheet({ card }: { card: GrantedCard }) {
  const { t } = useTranslation();
  if (card.currentVersionId === null) {
    return (
      <Paper sx={{ p: 2.5 }}>
        <Typography variant="body2">{t('rateCards.worksheet.noVersion')}</Typography>
      </Paper>
    );
  }
  return <WorksheetBody card={card} versionId={card.currentVersionId} />;
}

function WorksheetBody({ card, versionId }: { card: GrantedCard; versionId: number }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const url = useUrlState(SCHEMA);
  const currency = card.currency;
  // Annual is only a choice on a card with an annual price; tmm-api refuses it otherwise.
  const hasAnnual = card.annualBaseMinor !== null;
  // What the URL already shows. The toggle's draft is applied below and written only when sent.
  const urlMode: WorksheetMode = hasAnnual ? url.values.mode : 'MONTHLY';

  // The body last sent (debounced). The first one goes at once, with 0 for an absent count.
  const [sent, setSent] = useState<WorksheetRequest>(() => ({
    mode: urlMode,
    guests: url.values.guests ?? 0,
    connections: url.values.connections ?? 0,
    aiUsedMinor: url.values.ai,
  }));
  const load = useCallback(
    () => rateCardsApi.getWorksheet(card.id, versionId, sent),
    [card.id, versionId, sent],
  );
  const remote = useRemote(load);
  const worksheet = remote.data;

  // The last answer's terms give the defaults; before the first answer they are 0.
  // These are what the URL already shows. Typed text is a draft until it is valid and sent.
  const shownGuests = url.values.guests ?? worksheet?.terms.includedGuests ?? 0;
  const shownConnections = url.values.connections ?? worksheet?.terms.includedConnections ?? 0;
  const shownAi = url.values.ai;

  // The URL value this screen last wrote, so that round-trip does not clobber a newer keystroke.
  const written = useRef<{
    mode: WorksheetMode | null;
    guests: number | null;
    connections: number | null;
    ai: number | null;
  }>({ mode: null, guests: null, connections: null, ai: null });
  // The toggle stays responsive. An external URL change (Back, a link) replaces the draft,
  // unless this screen just wrote that mode and the user has already moved on.
  const [modeDraft, setModeDraft] = useState(urlMode);
  const [seenMode, setSeenMode] = useState(urlMode);
  if (seenMode !== urlMode) {
    setSeenMode(urlMode);
    if (written.current.mode !== urlMode) setModeDraft(urlMode);
  }
  const mode = modeDraft;

  const refs = {
    guests: useRef<HTMLInputElement | null>(null),
    connections: useRef<HTMLInputElement | null>(null),
    ai: useRef<HTMLInputElement | null>(null),
  };
  const fields = useFieldErrors(FIELD_ORDER, refs, false);
  const parseAi = (text: string): number | null => {
    const value = currency ? parseMoneyToMinor(text, currency, locale) : null;
    return value !== null && value >= 0 ? value : null;
  };
  const [guestsText, setGuestsText] = useDraft(
    shownGuests,
    String,
    parseCount,
    () => fields.clear('guests'),
    (value) => written.current.guests === value,
  );
  const [connectionsText, setConnectionsText] = useDraft(
    shownConnections,
    String,
    parseCount,
    () => fields.clear('connections'),
    (value) => written.current.connections === value,
  );
  const [aiText, setAiText] = useDraft(
    shownAi,
    (minor) => safeMoneyInput(minor, currency, locale),
    parseAi,
    () => fields.clear('ai'),
    (value) => written.current.ai === value,
  );
  const parsedGuests = parseCount(guestsText);
  const parsedConnections = parseCount(connectionsText);
  const parsedAi = parseAi(aiText);
  const guests = parsedGuests ?? shownGuests;
  const connections = parsedConnections ?? shownConnections;
  const aiUsedMinor = parsedAi ?? shownAi;
  const pending =
    mode !== sent.mode ||
    guests !== sent.guests ||
    connections !== sent.connections ||
    aiUsedMinor !== sent.aiUsedMinor;

  const countError = t('rateCards.worksheet.error.count', { max: MAX_COUNT });
  const aiError = t('rateCards.worksheet.error.ai', {
    example: moneyInputExample(currency, locale),
  });

  // An invalid value is marked as it is typed, and is never sent: the results stay those of the
  // last valid inputs, marked stale, until it is fixed (TMMRSL-0076).
  function changeCount(field: 'guests' | 'connections', text: string) {
    (field === 'guests' ? setGuestsText : setConnectionsText)(text);
    const value = parseCount(text);
    if (value === null) {
      fields.show(field, countError);
      return;
    }
    fields.clear(field);
  }

  function changeAi(text: string) {
    setAiText(text);
    const value = parseAi(text);
    if (value === null) {
      fields.show('ai', aiError);
      return;
    }
    fields.clear('ai');
  }

  // tmm-api's 400 VALIDATION_FAILED names fields: they are marked with the console's own words.
  const failure = remote.status === 'error' ? describeAction(remote.error, t) : null;
  const serverMarked =
    failure?.code === 'VALIDATION_FAILED' ? mapFieldErrors(failure.fieldKeys, SERVER_FIELDS) : {};
  const messages: Record<Field, string> = { guests: countError, connections: countError, ai: aiError };
  const errorOf = (field: Field): string | undefined =>
    fields.errors[field] ?? (serverMarked[field] ? messages[field] : undefined);
  const anyServerField = Object.keys(serverMarked).length > 0;

  const inputsId = useId();
  const invalidInput = parsedGuests === null || parsedConnections === null || parsedAi === null;
  // Only valid inputs are sent, and the URL is written in that same step: while one is invalid
  // the debounce waits, so typing "51" never sends or records the "5" on the way. Leaving a
  // field sends at once (TMMRSL-0077, TMMRSL-0082).
  const { set: setUrl } = url;
  const urlModeValue = url.values.mode;
  const publish = useCallback(
    (next: WorksheetRequest) => {
      setSent(next);
      const patch = sentInputPatch(next, {
        mode: urlModeValue,
        guests: shownGuests,
        connections: shownConnections,
        ai: shownAi,
      });
      if (patch) {
        if (patch.mode !== undefined) written.current.mode = patch.mode;
        if (patch.guests !== undefined) written.current.guests = patch.guests;
        if (patch.connections !== undefined) written.current.connections = patch.connections;
        if (patch.ai !== undefined) written.current.ai = patch.ai;
        setUrl(patch);
      }
    },
    [shownGuests, shownConnections, shownAi, urlModeValue, setUrl],
  );
  useEffect(() => {
    if (!pending || invalidInput) return undefined;
    const next: WorksheetRequest = { mode, guests, connections, aiUsedMinor };
    const id = setTimeout(() => publish(next), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [pending, invalidInput, mode, guests, connections, aiUsedMinor, publish]);
  function sendNow() {
    if (!pending || invalidInput) return;
    publish({ mode, guests, connections, aiUsedMinor });
  }
  const updating = remote.status === 'loading' || pending;
  const stale = worksheet !== null && (updating || invalidInput);
  let caption: string | null = null;
  if (stale && remote.status !== 'error') {
    caption = invalidInput
      ? t('rateCards.worksheet.staleInputs')
      : t('rateCards.worksheet.updating');
  }

  return (
    <Stack spacing={2.5}>
      <Paper component="section" aria-labelledby={inputsId} sx={{ p: 2.5 }}>
        <Typography id={inputsId} variant="subtitle1" component="h2" sx={{ mb: 1.5 }}>
          {t('rateCards.worksheet.inputs.title')}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'flex-start',
          }}
        >
          <ToggleButtonGroup
            exclusive
            size="small"
            value={mode}
            onChange={(_, next: WorksheetMode | null) => {
              if (next !== null) setModeDraft(next);
            }}
            aria-label={t('rateCards.worksheet.inputs.mode')}
          >
            <ToggleButton value="MONTHLY">{t('rateCards.worksheet.mode.MONTHLY')}</ToggleButton>
            <ToggleButton value="ANNUAL" disabled={!hasAnnual}>
              {t('rateCards.worksheet.mode.ANNUAL')}
            </ToggleButton>
          </ToggleButtonGroup>
          <TextField
            size="small"
            label={t('rateCards.worksheet.inputs.guests')}
            value={guestsText}
            inputRef={refs.guests}
            onChange={(event) => changeCount('guests', event.target.value)}
            onBlur={() => {
              if (parseCount(guestsText) === null) fields.show('guests', countError);
              sendNow();
            }}
            error={errorOf('guests') !== undefined}
            helperText={errorOf('guests')}
            slotProps={{ htmlInput: { inputMode: 'numeric' } }}
            sx={{ width: 200 }}
          />
          <TextField
            size="small"
            label={t('rateCards.worksheet.inputs.connections')}
            value={connectionsText}
            inputRef={refs.connections}
            onChange={(event) => changeCount('connections', event.target.value)}
            onBlur={() => {
              if (parseCount(connectionsText) === null) fields.show('connections', countError);
              sendNow();
            }}
            error={errorOf('connections') !== undefined}
            helperText={errorOf('connections')}
            slotProps={{ htmlInput: { inputMode: 'numeric' } }}
            sx={{ width: 220 }}
          />
          <TextField
            size="small"
            label={t('rateCards.worksheet.inputs.ai', { currency })}
            value={aiText}
            inputRef={refs.ai}
            onChange={(event) => changeAi(event.target.value)}
            onBlur={() => {
              if (parseAi(aiText) === null) fields.show('ai', aiError);
              sendNow();
            }}
            error={errorOf('ai') !== undefined}
            helperText={errorOf('ai')}
            slotProps={{ htmlInput: { inputMode: 'decimal' } }}
            sx={{ width: 280 }}
          />
        </Box>
      </Paper>

      {remote.status === 'error' && !anyServerField ? (
        <ApiErrorState what={t('errors.what.worksheet')} error={remote.error} onRetry={remote.reload} />
      ) : null}
      {/* Always mounted, so an error and a retry never drop the live region (TMMRSL-0076). */}
      <Box role="status" aria-live="polite" sx={{ minHeight: '1.5em' }}>
        {caption !== null ? (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {caption}
          </Typography>
        ) : null}
      </Box>
      {worksheet !== null && remote.status !== 'error' ? (
        <BusyRegion busy={stale}>
          <WorksheetResult worksheet={worksheet} currency={currency} />
        </BusyRegion>
      ) : null}
      {worksheet === null && remote.status === 'loading' ? (
        <Box role="status" aria-label={t('common.loading')}>
          <Skeleton variant="rounded" height={220} />
        </Box>
      ) : null}
    </Stack>
  );
}

function safeMoneyInput(minor: number, currency: string, locale: string): string {
  try {
    return minorToMoneyInput(minor, currency, locale);
  } catch {
    return String(minor);
  }
}

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <Paper component="section" aria-labelledby={id} sx={{ p: 2.5 }}>
      <Typography id={id} variant="subtitle1" component="h2" sx={{ mb: 1.5 }}>
        {title}
      </Typography>
      {children}
    </Paper>
  );
}

/** Each bill line's label (TMMAPI-0155 `WorksheetBillLine.kind`); anything else is `UNKNOWN`. */
const BILL_LABELS: Readonly<Record<WorksheetBillLine['kind'], string>> = {
  BASE: 'rateCards.worksheet.bill.line.BASE',
  GUESTS: 'rateCards.worksheet.bill.line.GUESTS',
  CONNECTIONS: 'rateCards.worksheet.bill.line.CONNECTIONS',
  AI_USAGE: 'rateCards.worksheet.bill.line.AI_USAGE',
  AI_CREDIT: 'rateCards.worksheet.bill.line.AI_CREDIT',
};

/** On a YEAR bill the subscription lines say what the year covers; AI lines read the same. */
const BILL_LABELS_YEAR: Readonly<Partial<Record<string, string>>> = {
  BASE: 'rateCards.worksheet.bill.lineYear.BASE',
  GUESTS: 'rateCards.worksheet.bill.lineYear.GUESTS',
  CONNECTIONS: 'rateCards.worksheet.bill.lineYear.CONNECTIONS',
};

function billLineLabel(kind: string, year: boolean, t: TFunction): string {
  const yearLabel = year && Object.hasOwn(BILL_LABELS_YEAR, kind) ? BILL_LABELS_YEAR[kind] : undefined;
  if (yearLabel !== undefined) return t(yearLabel);
  return t(
    Object.hasOwn(BILL_LABELS, kind)
      ? BILL_LABELS[kind as WorksheetBillLine['kind']]
      : 'rateCards.worksheet.bill.line.UNKNOWN',
  );
}

/** Each processing charge's kind (`ResellerWorksheetCharge.kind`); anything else is `UNKNOWN`. */
const CHARGE_KINDS: Readonly<Record<string, string>> = {
  BASE: 'rateCards.worksheet.steps.kind.BASE',
  ADD_ONS: 'rateCards.worksheet.steps.kind.ADD_ONS',
  RECHARGE: 'rateCards.worksheet.steps.kind.RECHARGE',
};

function chargeKindLabel(kind: string, t: TFunction): string {
  return t(
    Object.hasOwn(CHARGE_KINDS, kind)
      ? (CHARGE_KINDS[kind] as string)
      : 'rateCards.worksheet.steps.kind.UNKNOWN',
  );
}

const nowrap = { whiteSpace: 'nowrap' } as const;

/** A bold subtotal row of the bill: the label is its row header, the amount the server's. */
function SubtotalRow({ label, minor, currency }: { label: string; minor: number; currency: string }) {
  return (
    <TableRow sx={{ '& > *': { fontWeight: 600, color: 'text.primary' } }}>
      <TableCell component="th" scope="row">
        {label}
      </TableCell>
      <TableCell />
      <TableCell />
      <AccountingMoneyCell minor={minor} currency={currency} />
    </TableRow>
  );
}

/** The customer's bill, as sent (TMMAPI-0155 `bill`): one row per line, then the two subtotals. */
function BillTable({ bill, currency, labelledBy }: { bill: Bill; currency: string; labelledBy: string }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const dash = t('statements.unavailable');
  const year = bill.period === 'YEAR';
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" aria-labelledby={labelledBy}>
        <TableHead>
          <TableRow>
            <TableCell scope="col">{t('rateCards.worksheet.col.line')}</TableCell>
            <TableCell scope="col" align="right">
              {t('rateCards.worksheet.col.qty')}
            </TableCell>
            <TableCell scope="col" align="right">
              {t('rateCards.worksheet.col.unitPrice')}
            </TableCell>
            <TableCell scope="col" align="right">
              {t('rateCards.worksheet.col.amount')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {bill.lines.map((line, index) => {
            // A flat price is marked "(flat)", so it never reads as quantity × price (TMMRSL-0077).
            let unit: string | null = null;
            if (line.unitPriceMinor !== undefined) {
              unit = moneyIn(line.unitPriceMinor, currency, locale, dash);
            } else if (line.flatMinor !== undefined) {
              unit = t('rateCards.worksheet.bill.flat', {
                amount: moneyIn(line.flatMinor, currency, locale, dash),
              });
            }
            return (
              // Lines have no identity of their own; a row is its position in the answer.
              <TableRow key={`${line.kind}-${index}`}>
                <TableCell component="th" scope="row">
                  {billLineLabel(line.kind, year, t)}
                </TableCell>
                <TableCell align="right" sx={nowrap}>
                  {line.quantity === undefined ? null : formatCount(line.quantity, locale)}
                </TableCell>
                <TableCell align="right" sx={nowrap}>
                  {unit}
                </TableCell>
                <AccountingMoneyCell minor={line.amountMinor} currency={currency} />
              </TableRow>
            );
          })}
          <SubtotalRow
            label={
              year
                ? t('rateCards.worksheet.bill.subtotalYear')
                : t('rateCards.worksheet.bill.subtotalMonth')
            }
            minor={bill.subscriptionSubtotalMinor}
            currency={currency}
          />
          <SubtotalRow
            label={t('rateCards.worksheet.bill.aiNet')}
            minor={bill.aiNetMinor}
            currency={currency}
          />
        </TableBody>
      </Table>
    </Box>
  );
}

function StepItem({ children }: { children: ReactNode }) {
  return (
    <Typography component="li" variant="body2" sx={{ color: 'text.secondary' }}>
      {children}
    </Typography>
  );
}

/**
 * "How it's worked out": the card's terms, then the steps behind the reseller's lines exactly as
 * sent (TMMAPI-0155 `steps`). Counts and amounts are the server's; nothing is divided or summed.
 */
function WorkedOut({
  worksheet,
  currency,
  id,
}: {
  worksheet: Worksheet;
  currency: string;
  id: string;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const dash = t('statements.unavailable');
  const money = (minor: number) => moneyIn(minor, currency, locale, dash);
  const accounting = (minor: number) => {
    if (!currency) return dash;
    try {
      return formatMoneyAccounting(minor, currency, locale);
    } catch {
      return dash;
    }
  };
  const { terms, steps } = worksheet;
  let feeRate: string | null = null;
  if (steps.feePercentBp !== null && steps.feeFixedMinor !== null) {
    feeRate = t('rateCards.worksheet.steps.feeBoth', {
      percent: bpToPercentText(steps.feePercentBp, locale),
      fixed: accounting(steps.feeFixedMinor),
    });
  } else if (steps.feePercentBp !== null) {
    feeRate = t('rateCards.worksheet.steps.feePercent', {
      percent: bpToPercentText(steps.feePercentBp, locale),
    });
  } else if (steps.feeFixedMinor !== null) {
    feeRate = t('rateCards.worksheet.steps.feeFixed', { fixed: accounting(steps.feeFixedMinor) });
  }

  return (
    <Paper
      component="section"
      aria-labelledby={id}
      variant="outlined"
      sx={{ p: 2, bgcolor: 'action.hover' }}
    >
      <Typography id={id} variant="subtitle2" component="h2" sx={{ mb: 1 }}>
        {t('rateCards.worksheet.terms.title')}
      </Typography>
      <Stack component="ul" spacing={0.5} sx={{ m: 0, paddingInlineStart: 2.5 }}>
        <StepItem>{t('rateCards.worksheet.terms.guests', { count: terms.includedGuests })}</StepItem>
        <StepItem>
          {t('rateCards.worksheet.terms.connections', { count: terms.includedConnections })}
        </StepItem>
        <StepItem>
          {t('rateCards.worksheet.terms.credit', { amount: money(terms.aiMonthlyCreditMinor) })}
        </StepItem>
        <StepItem>
          {t('rateCards.worksheet.steps.chargesPerYear', {
            count: steps.subscriptionChargesPerYear,
          })}
        </StepItem>
        {steps.charges.map((charge, index) => (
          // Charges have no identity of their own; an item is its position in the answer.
          <StepItem key={`${charge.kind}-${index}`}>
            {t('rateCards.worksheet.steps.charge', {
              kind: chargeKindLabel(charge.kind, t),
              gross: accounting(charge.grossMinor),
              count: charge.perYear,
              fee: accounting(charge.feeMinor),
              share: accounting(charge.resellerShareMinor),
            })}
          </StepItem>
        ))}
        {feeRate !== null ? <StepItem>{feeRate}</StepItem> : null}
        <StepItem>{t('rateCards.worksheet.steps.recharges', { count: steps.recharges })}</StepItem>
        <StepItem>
          {t('rateCards.worksheet.steps.creditCovered', {
            amount: accounting(steps.creditCoveredMinor),
          })}
        </StepItem>
        <StepItem>
          {t('rateCards.worksheet.steps.creditAbsorbs', {
            amount: accounting(steps.creditResellerAbsorbsMinor),
          })}
        </StepItem>
        <StepItem>{t('rateCards.worksheet.terms.asSent')}</StepItem>
      </Stack>
    </Paper>
  );
}

function WorksheetResult({ worksheet, currency }: { worksheet: Worksheet; currency: string }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const ids = useId();
  const annual = worksheet.mode === 'ANNUAL';
  const lineCell = (line: WorksheetLine) => (
    <TableCell component="th" scope="row">
      {lineLabel(line.line, t)}
    </TableCell>
  );
  const billTitle =
    worksheet.bill.period === 'YEAR'
      ? t('rateCards.worksheet.bill.titleYear')
      : t('rateCards.worksheet.bill.titleMonth');
  const earnTitle = annual
    ? t('rateCards.worksheet.earn.titleAnnual')
    : t('rateCards.worksheet.earn.title');

  return (
    <Stack spacing={2.5}>
      <Section id={`${ids}-bill`} title={billTitle}>
        <BillTable bill={worksheet.bill} currency={currency} labelledBy={`${ids}-bill`} />
      </Section>

      <WorkedOut worksheet={worksheet} currency={currency} id={`${ids}-terms`} />

      <Section id={`${ids}-earn`} title={earnTitle}>
        <DefinitionList>
          <DefinitionField label={t('rateCards.worksheet.earn.customerPays')}>
            <AccountingAmount minor={worksheet.totals.customerPaysMinor} currency={currency} />
          </DefinitionField>
          <DefinitionField label={t('rateCards.worksheet.earn.profit')}>
            <AccountingAmount minor={worksheet.totals.resellerProfitMinor} currency={currency} />
            {worksheet.resellerProfitBp === null ? null : (
              <Typography
                variant="body2"
                component="span"
                sx={{ display: 'block', color: 'text.secondary' }}
              >
                {t('rateCards.worksheet.earn.profitPercent', {
                  percent: bpToPercentText(worksheet.resellerProfitBp, locale),
                })}
              </Typography>
            )}
          </DefinitionField>
        </DefinitionList>
        <Box sx={{ overflowX: 'auto', mt: 2 }}>
          <Table size="small" aria-labelledby={`${ids}-earn`}>
            <TableHead>
              <TableRow>
                <TableCell scope="col">{t('rateCards.worksheet.col.line')}</TableCell>
                <TableCell scope="col" align="right">
                  {t('rateCards.worksheet.col.customerPays')}
                </TableCell>
                <TableCell scope="col" align="right">
                  {t('rateCards.worksheet.col.costs')}
                </TableCell>
                <TableCell scope="col" align="right">
                  {t('rateCards.worksheet.col.profit')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {worksheet.lines.map((line, index) => (
                <TableRow key={`${line.line}-${index}`}>
                  {lineCell(line)}
                  <AccountingMoneyCell minor={line.customerPaysMinor} currency={currency} />
                  <AccountingMoneyCell minor={line.resellerCostsMinor} currency={currency} />
                  <AccountingMoneyCell minor={line.resellerProfitMinor} currency={currency} />
                </TableRow>
              ))}
            </TableBody>
            <TotalsFooter>
              <TableCell component="th" scope="row">
                {t('rateCards.worksheet.total')}
              </TableCell>
              <AccountingMoneyCell minor={worksheet.totals.customerPaysMinor} currency={currency} />
              <AccountingMoneyCell minor={worksheet.totals.resellerCostsMinor} currency={currency} />
              <AccountingMoneyCell minor={worksheet.totals.resellerProfitMinor} currency={currency} />
            </TotalsFooter>
          </Table>
        </Box>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1.5 }}>
          {t('rateCards.worksheet.note')}
        </Typography>
      </Section>
    </Stack>
  );
}
